import {
  BelegStatusDTO,
  BelegVersandstatusDTO,
  VersandStatusDTO,
} from '../openapi/fakturierung-openapi';


/**
 * CSS-Key fuer den Versandstatus. Wird fuer die Visualisierung im UI verwendet.
 */
export type VersandstatusCssKey =
  | 'versendet'
  | 'fehlgeschlagen'
  | 'offen'
  | 'nicht-versendet'
  | 'nicht-versandfaehig';

/**
 * Discriminator-Werte des VersandStatus-Schemas (OpenAPI `oneOf` mit
 * `propertyName: typ`). Die Konstanten werden zentral hier definiert und
 * exportiert, damit das Mapping nicht an mehreren Stellen dupliziert werden
 * muss. Sobald der OpenAPI-Generator hierfuer ein Enum erzeugt, sollten
 * diese Konstanten direkt aus dem generierten Enum bezogen werden.
 */
export const TYP_OFFEN = 'VersandStatusOffen';
export const TYP_VERSENDET = 'VersandStatusVersendet';
export const TYP_FEHLGESCHLAGEN = 'VersandStatusFehlgeschlagen';

/**
 * Liest den Discriminator-Typ aus dem VersandStatus-Objekt.
 *
 * Beim `typescript-angular` Generator kommt der Union-Typ als
 * Intersection der drei Varianten zurueck — `status.typ` ist damit korrekt
 * typisiert, wir casten hier nur defensiv auf `string`, weil das Feld in
 * Mocks auch mal fehlen darf.
 */
export function getVersandstatusTyp(status: VersandStatusDTO | undefined): string | undefined {
  return (status as { typ?: string } | undefined)?.typ;
}

/**
 * Alias-Export fuer Rueckwaertskompatibilitaet im Modul.
 */
const getTyp = getVersandstatusTyp;

/**
 * Liefert `true`, wenn der Versandauftrag aktuell offen (in Versand) ist.
 */
export function isOffen(status: VersandStatusDTO | undefined): boolean {
  return getTyp(status) === TYP_OFFEN;
}

/**
 * Liefert `true`, wenn der Versandauftrag erfolgreich zugestellt wurde.
 */
export function isVersendet(status: VersandStatusDTO | undefined): boolean {
  return getTyp(status) === TYP_VERSENDET;
}

/**
 * Liefert `true`, wenn der Versandauftrag fehlgeschlagen ist.
 */
export function isFehlgeschlagen(status: VersandStatusDTO | undefined): boolean {
  return getTyp(status) === TYP_FEHLGESCHLAGEN;
}

/**
 * Liefert die Fehlermeldung eines fehlgeschlagenen Versands, ansonsten `null`.
 */
export function getFehlermeldung(status: VersandStatusDTO | undefined): string | null {
  if (!isFehlgeschlagen(status)) {
    return null;
  }
  const fehlermeldung = (status as { fehlermeldung?: string } | undefined)?.fehlermeldung;
  return fehlermeldung ?? null;
}

/**
 * Liefert den Zeitpunkt des aktuellen Status eines Versandeintrags als
 * ISO-String.
 * Bedeutung pro Status:
 *  - `VersandStatusOffen`         → Zeitpunkt der Auftragserstellung
 *  - `VersandStatusVersendet`     → Zeitpunkt des erfolgreichen Versands
 *  - `VersandStatusFehlgeschlagen`→ Zeitpunkt des Fehlschlags
 */
export function getStatusZeitpunkt(eintrag: BelegVersandstatusDTO | undefined): string | null {
  return eintrag?.zeitpunkt ?? null;
}

/**
 * Liefert das fuer die UI anzuzeigende Label fuer einen Versandstatus unter
 * Beruecksichtigung des Belegstatus (Entwurf hat Vorrang vor dem Versandstatus).
 *
 * @param status Der aktuelle Versandstatus des Belegs (oder `undefined`).
 * @param belegStatus Der Belegstatus (offen/festgeschrieben/storniert).
 * @returns Das Label fuer die UI-Anzeige.
 */
export function getVersandstatusLabel(
  status: VersandStatusDTO | undefined,
  belegStatus: BelegStatusDTO | undefined,
): string {
  if (belegStatus?.offen) {
    return 'Nicht versandfähig';
  }
  if (isVersendet(status)) {
    return 'Versendet';
  }
  if (isFehlgeschlagen(status)) {
    return 'Fehlgeschlagen';
  }
  if (isOffen(status)) {
    return 'In Versand';
  }
  return 'Nicht versendet';
}

/**
 * Liefert den CSS-Key fuer einen Versandstatus unter Beruecksichtigung des
 * Belegstatus (Entwurf hat Vorrang vor dem Versandstatus).
 *
 * @param status Der aktuelle Versandstatus des Belegs (oder `undefined`).
 * @param belegStatus Der Belegstatus (offen/festgeschrieben/storniert).
 * @returns Der CSS-Key fuer die Styling-Zuordnung.
 */
export function getVersandstatusCssKey(
  status: VersandStatusDTO | undefined,
  belegStatus: BelegStatusDTO | undefined,
): VersandstatusCssKey {
  if (belegStatus?.offen) {
    return 'nicht-versandfaehig';
  }
  if (isVersendet(status)) {
    return 'versendet';
  }
  if (isFehlgeschlagen(status)) {
    return 'fehlgeschlagen';
  }
  if (isOffen(status)) {
    return 'offen';
  }
  return 'nicht-versendet';
}
